/**
 * La reproducción del «video» de la obra: la fecha `t` avanza en cada cuadro, pero el modelo se repinta cada
 * `MS_ENTRE_REPINTADOS` (y el intervalo se estira si el repintado tarda: modelos grandes). La usan el diálogo de
 * staff (`AvanceDeObra`) y la página pública del enlace para el cliente.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MS_ENTRE_REPINTADOS,
  pasoDeReproduccion,
  type VelocidadDelVideo,
} from '../../lib/avanceObra4d.js';

export interface RangoDeFechas {
  desde: number;
  hasta: number;
}

export function useReproduccionDeAvance(rango: RangoDeFechas | null) {
  const [t, setT] = useState<number | null>(null);
  const tRef = useRef(0);
  const [reproduciendo, setReproduciendo] = useState(false);
  const [velocidad, setVelocidad] = useState<VelocidadDelVideo>('normal');

  // Se abre en el estado FINAL de la obra (lo más que se ha construido); «Reproducir» la vuelve a armar desde el principio.
  useEffect(() => {
    if (rango) {
      tRef.current = rango.hasta;
      setT(rango.hasta);
    }
  }, [rango]);

  const elegirFecha = useCallback((v: number) => {
    tRef.current = v;
    setT(v);
  }, []);

  useEffect(() => {
    if (!reproduciendo || !rango) return;
    let raf = 0;
    let previo = performance.now();
    let ultimoPintado = 0;
    // Qué tan seguido se repinta se adapta al modelo: un repintado que tarda más de lo previsto (modelo grande) estira el intervalo,
    // para que los repintados no se encadenen y la reproducción no se trabe.
    let entreRepintados = MS_ENTRE_REPINTADOS;
    let pintoEn = -1;
    const bucle = (ahora: number) => {
      const dt = (ahora - previo) / 1000;
      previo = ahora;
      if (pintoEn >= 0) {
        entreRepintados = Math.min(500, Math.max(MS_ENTRE_REPINTADOS, 2 * (ahora - pintoEn)));
        pintoEn = -1;
      }
      const paso = pasoDeReproduccion(tRef.current, dt, rango, velocidad);
      tRef.current = paso.t;
      if (paso.termino || ahora - ultimoPintado >= entreRepintados) {
        ultimoPintado = ahora;
        pintoEn = ahora;
        setT(paso.t);
      }
      if (paso.termino) {
        setReproduciendo(false);
        return;
      }
      raf = requestAnimationFrame(bucle);
    };
    raf = requestAnimationFrame(bucle);
    return () => cancelAnimationFrame(raf);
  }, [reproduciendo, rango, velocidad]);

  const pausar = useCallback(() => setReproduciendo(false), []);

  /** Al final vuelve a empezar. `alIniciar` corre solo al ARRANCAR (no al pausar). */
  const alternar = useCallback(
    (alIniciar?: () => void) => {
      if (!rango) return;
      if (!reproduciendo && tRef.current >= rango.hasta) elegirFecha(rango.desde);
      if (!reproduciendo) alIniciar?.();
      setReproduciendo((r) => !r);
    },
    [rango, reproduciendo, elegirFecha],
  );

  /** Arrastrar el deslizador o tocar la curva: pausa y salta a esa fecha. */
  const saltarA = useCallback(
    (v: number) => {
      setReproduciendo(false);
      elegirFecha(v);
    },
    [elegirFecha],
  );

  /** Al cerrar el diálogo: todo de cero. */
  const reiniciar = useCallback(() => {
    setT(null);
    setReproduciendo(false);
  }, []);

  return { t, reproduciendo, velocidad, setVelocidad, elegirFecha, saltarA, pausar, alternar, reiniciar };
}
